/**
 * Sort + date-range controls shared by Library Videos and Library Posts
 * (2026-09-14). One component so the two pages behave and look identical:
 *
 *   [ Newest | Oldest ]   From [date] – To [date]  ×
 *
 * The order is a remembered per-page preference (the parent owns the state
 * and persists it via `writeSortPref`); the range is session-only on
 * purpose — see web/src/lib/librarySort.ts for both decisions.
 *
 * Purely presentational: the parent applies `applyDateView` to its own list.
 */
import { ArrowDownWideNarrow, ArrowUpNarrowWide, X } from "lucide-react";
import type { DateRange, SortOrder } from "../lib/librarySort.ts";
import { hasDateRange } from "../lib/librarySort.ts";

interface Props {
  order: SortOrder;
  onOrder: (order: SortOrder) => void;
  range: DateRange;
  onRange: (range: DateRange) => void;
  /** What the date axis is, for the tooltip — "rendered" / "generated". */
  dateLabel: string;
  /** Stack the range under the toggle on narrow screens. */
  compact?: boolean;
}

const chip = (active: boolean): React.CSSProperties => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 12px",
  borderRadius: 999,
  border: `1px solid ${active ? "rgba(255,213,0,.30)" : "rgba(255,255,255,.08)"}`,
  background: active ? "rgba(255,213,0,.10)" : "rgba(255,255,255,.02)",
  color: active ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
  fontFamily: "inherit",
  fontSize: 11,
  fontWeight: 700,
  cursor: "pointer",
});

const dateInput: React.CSSProperties = {
  padding: "6px 10px",
  borderRadius: 10,
  background: "var(--ql-input)",
  border: "1px solid rgba(255,255,255,.08)",
  color: "var(--ql-fg)",
  fontSize: 12,
  fontFamily: "var(--ql-font-mono)",
  colorScheme: "dark",
  boxSizing: "border-box",
  minWidth: 0,
};

export function LibraryDateControls({
  order,
  onOrder,
  range,
  onRange,
  dateLabel,
  compact = false,
}: Props): React.JSX.Element {
  const active = hasDateRange(range);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
        width: compact ? "100%" : undefined,
      }}
    >
      <div role="group" aria-label="Sort order" style={{ display: "inline-flex", gap: 4 }}>
        <button
          type="button"
          className="ql-chip"
          aria-pressed={order === "newest"}
          onClick={() => onOrder("newest")}
          title={`Newest ${dateLabel} first`}
          style={chip(order === "newest")}
        >
          <ArrowDownWideNarrow size={12} strokeWidth={2} />
          Newest
        </button>
        <button
          type="button"
          className="ql-chip"
          aria-pressed={order === "oldest"}
          onClick={() => onOrder("oldest")}
          title={`Oldest ${dateLabel} first — recycle what's been sitting longest`}
          style={chip(order === "oldest")}
        >
          <ArrowUpNarrowWide size={12} strokeWidth={2} />
          Oldest
        </button>
      </div>

      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          width: compact ? "100%" : undefined,
        }}
      >
        <label
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: ".14em",
            color: "var(--ql-fg-subtle)",
            textTransform: "uppercase",
          }}
        >
          {dateLabel}
        </label>
        <input
          type="date"
          value={range.from ?? ""}
          max={range.to || undefined}
          onChange={(e) => onRange({ ...range, from: e.target.value })}
          aria-label={`${dateLabel} from`}
          style={{ ...dateInput, flex: compact ? 1 : undefined }}
        />
        <span style={{ color: "var(--ql-fg-subtle)", fontSize: 12 }}>–</span>
        <input
          type="date"
          value={range.to ?? ""}
          min={range.from || undefined}
          onChange={(e) => onRange({ ...range, to: e.target.value })}
          aria-label={`${dateLabel} to`}
          style={{ ...dateInput, flex: compact ? 1 : undefined }}
        />
        {active && (
          <button
            type="button"
            onClick={() => onRange({})}
            aria-label="Clear date range"
            title="Clear date range"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 26,
              height: 26,
              borderRadius: 999,
              border: "1px solid rgba(255,213,0,.30)",
              background: "rgba(255,213,0,.10)",
              color: "var(--ql-yellow)",
              cursor: "pointer",
              padding: 0,
            }}
          >
            <X size={12} strokeWidth={2.5} />
          </button>
        )}
      </div>
    </div>
  );
}
